'use client';

import React, { useMemo } from 'react';
import {
  Label,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
} from 'recharts';

import { ChartContainer, type ChartConfig } from '@repo/ui/components/ui/chart';

import { useResponsiveRadialChartDimensions } from '@/lib/chart-utils';
import { usePlatform } from '@/hooks/use-platform';
import { useAnalysisContainer } from '@/components/article-analysis/analysis-container';

interface CredibleChartProps {
  credibilityScore?: number;
}

export const CredibleChart: React.FC<CredibleChartProps> = ({
  credibilityScore,
}) => {
  const { isWebkit } = usePlatform();

  const chartDimensions = useResponsiveRadialChartDimensions(
    'large',
    useAnalysisContainer(),
  );

  const credibleScoreChart = {
    chartData: [
      {
        type: 'credible',
        score: Number.isFinite(credibilityScore)
          ? Math.round(credibilityScore || 0)
          : 0,
        fill: 'hsl(var(--facts-pie-chart))',
      },
    ],
    chartConfig: {
      score: {
        label: 'Score',
      },
      credible: {
        label: 'Credible',
        color: 'hsl(var(--facts-pie-chart))',
      },
    } satisfies ChartConfig,
  };

  const endAngle = useMemo(() => {
    return credibilityScore ? 90 + credibilityScore * 3.6 : 90;
  }, [credibilityScore]);

  const labelsY = useMemo(() => {
    if (chartDimensions.innerRadius >= 57) {
      return {
        top: 10,
        bottom: 13,
      };
    }
    if (chartDimensions.innerRadius >= 43) {
      return {
        top: 8,
        bottom: 11,
      };
    }
    if (chartDimensions.innerRadius >= 40) {
      return {
        top: 6,
        bottom: 9,
      };
    }
    return {
      top: 6,
      bottom: 9,
    };
  }, [chartDimensions.innerRadius]);

  return (
    <ChartContainer
      config={credibleScoreChart.chartConfig}
      className="aspect-square max-h-20 w-full @cred-md:max-h-22.75 @cred-lg:max-h-24.75 @cred-xl:max-h-32.5 [&_.recharts-radial-bar-background-sector]:fill-background"
    >
      <RadialBarChart
        data={credibleScoreChart.chartData}
        startAngle={90}
        endAngle={endAngle}
        innerRadius={chartDimensions.innerRadius}
        outerRadius={chartDimensions.outerRadius}
      >
        <PolarGrid
          gridType="circle"
          radialLines={false}
          stroke="none"
          className="first:fill-[#333333] last:fill-background"
          polarRadius={chartDimensions.polarRadius}
        />
        <RadialBar dataKey="score" background cornerRadius={10} />
        <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
          <Label
            content={({ viewBox }) => {
              if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                return (
                  <text
                    x={viewBox.cx}
                    y={viewBox.cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    <tspan
                      x={viewBox.cx}
                      y={
                        isWebkit
                          ? (viewBox.cy || 0) - 1
                          : (viewBox.cy || 0) - labelsY.top
                      }
                      className="fill-neutral-1 text-lg font-bold leading-5.25 @cred-md:text-xl @cred-md:leading-5.75 @cred-lg:text-[22px] @cred-lg:leading-5.75 @cred-xl:text-[30px] @cred-xl:leading-8.75"
                    >
                      {credibleScoreChart.chartData[0]?.score + '%'}
                    </tspan>
                    <tspan
                      x={viewBox.cx}
                      y={
                        isWebkit
                          ? (viewBox.cy || 0) + labelsY.bottom * 1.35
                          : (viewBox.cy || 0) + labelsY.bottom
                      }
                      className="fill-neutral-1 text-[11px] font-bold @cred-md:text-xs @cred-lg:text-[13px] @cred-xl:text-[17px]"
                    >
                      Confidence
                    </tspan>
                  </text>
                );
              }
            }}
          />
        </PolarRadiusAxis>
      </RadialBarChart>
    </ChartContainer>
  );
};
